import {
  CONNECTIVITY_TARGETS,
  CONNECTIVITY_TIMEOUT_MS,
  ConnectivityTarget,
  CORE_TARGETS,
  ProbeKind,
} from 'lib/constants/connectivity'

export interface ProbeResult {
  target: ConnectivityTarget
  reachable: boolean
  /** Short, operator-facing reason. Empty when reachable. */
  detail: string
}

type Outcome = { reachable: boolean; detail: string }

const OK: Outcome = { reachable: true, detail: '' }
const TIMED_OUT: Outcome = { reachable: false, detail: 'timed out' }
const REFUSED: Outcome = { reachable: false, detail: 'could not connect' }

// A readable cross-origin request. Reserved for our own hosts, which send CORS
// headers — any HTTP status that comes back proves the request reached the
// server, so only a transport-level failure counts as unreachable. A CORS
// rejection also lands here, which is correct: if our own API stops sending
// those headers through a customer's proxy, the portal is just as broken.
async function probeHttp(url: string, timeoutMs: number): Promise<Outcome> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    await fetch(url, { method: 'GET', cache: 'no-store', signal: controller.signal })
    return OK
  } catch {
    return controller.signal.aborted ? TIMED_OUT : REFUSED
  } finally {
    clearTimeout(timer)
  }
}

// An opaque request, for third-party hosts that send us no CORS headers.
// Resolving proves DNS, TCP 443 and TLS to the host all succeeded, which is
// what an allowlist governs. The status is unreadable, so a proxy answering
// with its own block page resolves too and reads as reachable.
async function probeNoCors(url: string, timeoutMs: number): Promise<Outcome> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    await fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store', signal: controller.signal })
    return OK
  } catch {
    return controller.signal.aborted ? TIMED_OUT : REFUSED
  } finally {
    clearTimeout(timer)
  }
}

// A real handshake against the real endpoint — the closest thing to what the
// app does in anger, and the case allowlists most often miss, since the
// upgrade can be blocked while plain 443 is open.
function probeWebSocket(url: string, timeoutMs: number): Promise<Outcome> {
  return new Promise<Outcome>(resolve => {
    let settled = false
    let socket: WebSocket | undefined

    const finish = (outcome: Outcome) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      // Never leave the probe socket open; the app opens its own.
      try {
        socket?.close()
      } catch {
        /* already closing */
      }
      resolve(outcome)
    }

    const timer = setTimeout(() => finish(TIMED_OUT), timeoutMs)

    try {
      socket = new WebSocket(url)
    } catch {
      finish(REFUSED)
      return
    }

    // `open` firing first is the only unambiguous success. `error` carries no
    // detail by design — the spec withholds it so a page cannot probe the
    // network it is on — so a blocked port and a refused handshake are
    // indistinguishable, and both mean "not usable from here".
    socket.onopen = () => finish(OK)
    socket.onerror = () => finish(REFUSED)
    socket.onclose = event => finish(event.wasClean ? OK : { reachable: false, detail: `closed (${event.code})` })
  })
}

function probe(kind: ProbeKind, url: string, timeoutMs: number): Promise<Outcome> {
  switch (kind) {
    case 'http':
      return probeHttp(url, timeoutMs)
    case 'no-cors':
      return probeNoCors(url, timeoutMs)
    case 'ws':
      return probeWebSocket(url, timeoutMs)
  }
}

/**
 * Probes every dependency the portal needs to reach and reports each one.
 *
 * Runs the probes concurrently and never rejects: a probe that throws is a
 * failed check, not a failed run, because a half-finished report is worse than
 * a complete one when the whole point is telling an IT team which hosts to
 * allow.
 */
export async function runConnectivityChecks(
  targets: ConnectivityTarget[] = CORE_TARGETS,
  timeoutMs: number = CONNECTIVITY_TIMEOUT_MS,
): Promise<ProbeResult[]> {
  return Promise.all(
    targets.map(async target => {
      const outcome = await probe(target.kind, target.url, timeoutMs).catch(() => REFUSED)
      return { target, ...outcome }
    }),
  )
}

/**
 * Design-time preview.
 *
 * Add `?connectivity-preview` to any portal URL and the overlay renders with
 * every target reported blocked, without a single probe being sent. The panel
 * is otherwise unreachable from a machine where the network works, which is
 * every machine we develop on.
 *
 * Values, all optional:
 *
 * - absent          off. Normal probing.
 * - `` / `all`      every target, core and voice.
 * - `core` `voice`  one group.
 * - `pusher,zello`  named target ids, comma separated.
 *
 * Unknown values fall back to every target rather than an empty panel, since an
 * empty panel is indistinguishable from the preview not working.
 */
const PREVIEW_PARAM = 'connectivity-preview'

// Cycled across the preview rows so each detail wording the panel can render —
// a refusal, a timeout, a socket close code — is on screen at the same time.
const PREVIEW_DETAILS = ['could not connect', 'timed out', 'closed (1006)']

export function previewTargets(search: string = window.location.search): ConnectivityTarget[] | null {
  const value = new URLSearchParams(search).get(PREVIEW_PARAM)
  if (value === null) return null

  const keys = value
    .toLowerCase()
    .split(',')
    .map(key => key.trim())
    .filter(Boolean)
  if (!keys.length || keys.includes('all') || keys.includes('1') || keys.includes('true')) return CONNECTIVITY_TARGETS

  const selected = CONNECTIVITY_TARGETS.filter(target => keys.includes(target.id) || keys.includes(target.group))
  return selected.length ? selected : CONNECTIVITY_TARGETS
}

/** Fabricated blocked results for the preview, or null when it is off. */
export function previewResults(search?: string): ProbeResult[] | null {
  const targets = previewTargets(search)
  if (!targets) return null
  return targets.map((target, index) => ({
    target,
    reachable: false,
    detail: PREVIEW_DETAILS[index % PREVIEW_DETAILS.length],
  }))
}

/** The unreachable subset, ordered so core failures read before voice ones. */
export function unreachable(results: ProbeResult[]): ProbeResult[] {
  const weight = (result: ProbeResult) => (result.target.group === 'core' ? 0 : 1)
  return results.filter(result => !result.reachable).sort((a, b) => weight(a) - weight(b))
}

/** Plain-text report an operator can paste into a ticket for their IT team. */
export function formatReport(results: ProbeResult[]): string {
  const lines = results.map(({ target, reachable, detail }) => {
    const status = reachable ? 'OK     ' : 'BLOCKED'
    const suffix = reachable ? '' : ` — ${detail}`
    return `${status}  ${target.label}  ${target.url}${suffix}`
  })
  return [`React Mobile portal connectivity check — ${new Date().toISOString()}`, ...lines].join('\n')
}
